import styled from '@emotion/styled';
import { Model, TableResult } from '@gameonly/core';
import { useTranslation } from 'react-i18next';
import keyify from '../project/parameters/keyify';

const PdfTable = styled.table<{ compact?: boolean }>`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  td,
  th {
    font-size: ${({ compact }) => (compact ? '0.7rem' : '1.2rem')};
    padding: ${({ compact }) => (compact ? '6px' : '24px 12px')};
    border: 0px solid white;
    background-clip: padding-box;
    text-align: center;
  }

  td {
    background-color: var(--grey);
  }

  th {
    font-size: ${({ compact }) => (compact ? '0.8rem' : '1.5rem')};
  }

  tr > th {
    border-bottom-width: ${({ compact }) => (compact ? '0.1rem' : '0.6rem')};
  }
  tr > th + th,
  tr > td + td {
    border-left-width: ${({ compact }) => (compact ? '0.1rem' : '0.6rem')};
  }
  tr + tr > td {
    border-top-width: ${({ compact }) => (compact ? '0.1rem' : '0.6rem')};
  }
`;

const isCategory = (row: string[]) => {
  return row.slice(1).every((value) => !value);
};

export const Table = ({
  result,
  model,
  section,
}: {
  result: TableResult;
  model?: Model;
  section?: {
    title: string;
    index: number;
  };
}) => {
  const { t } = useTranslation(['model']);
  if (!result.table || !result.table.length) return null;

  const table = result.table;
  const isCompact = table.length > 8; // total rows including header

  const [headerRow, ...dataRows] = table;
  const gesColumnIndex = headerRow.findIndex(
    (header, index) => index > 0 && /ges/i.test(header),
  );

  const headers = [headerRow[0], headerRow[gesColumnIndex]];
  const data = dataRows.map((row) => [row[0], row[gesColumnIndex]]);

  const translationPrefix = `model:${model?.name.toLowerCase()}.result.${result.code?.toLowerCase()}`;

  return (
    <PdfTable compact={isCompact}>
      <thead>
        <tr style={{ backgroundColor: 'var(--darkblue)', color: 'white' }}>
          {headers.map((header, i) => (
            <th key={i}>
              {t(`${translationPrefix}.data.${keyify(header)}`).toLocaleUpperCase()}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, index) => {
          if (isCategory(row)) {
            return (
              <tr
                key={index}
                style={{
                  backgroundColor: 'var(--pdf-lightgreen)',
                  color: 'white',
                }}
              >
                <td colSpan={row.length}>
                  {t(`${translationPrefix}.data.${keyify(row[0])}`)}
                </td>
              </tr>
            );
          }
          return (
            <tr
              key={index}
              style={{ backgroundColor: 'var(--pdf-lightestgreen)' }}
            >
              {row.map((value, i) => {
                if (i === 0) {
                  return (
                    <td key={i}>
                      {t(`${translationPrefix}.data.${keyify(value)}`)}
                    </td>
                  );
                }
                return <td key={i}>{value}</td>;
              })}
            </tr>
          );
        })}
      </tbody>
    </PdfTable>
  );
};
